import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, X, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const formatRelative = (iso) => {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
};

const CheckInPanel = () => {
  const [stats, setStats] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const loginURL = `${window.location.origin}/login`;

  const load = async () => {
    try {
      const res = await api.get("/admin/checkin-stats");
      setStats(res.data);
    } catch (err) {
      console.warn("Check-in stats refresh failed", err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10" data-testid="checkin-panel">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="overline">Live Event Check-In</div>
          <h2 className="font-serif-display text-3xl mt-1">Welcome desk</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-md">
            Display the QR code at the host table. Attendees scan, enter their email once, and they're in.
            This panel auto-refreshes every 5 seconds.
          </p>
        </div>
        <button
          onClick={() => setShowQR(true)}
          data-testid="show-qr-button"
          className="mm-btn-primary"
          style={{ width: "auto", padding: "12px 20px", fontSize: 13 }}
        >
          <QrCode size={14} strokeWidth={1.5} className="inline mr-2" />
          Show check-in QR
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Signed up" value={stats?.total ?? "—"} testid="checkin-total" />
        <Stat label="Profile complete" value={stats?.profile_complete ?? "—"} testid="checkin-complete" />
        <Stat label="Last 5 min" value={stats?.last_5_min ?? "—"} accent testid="checkin-recent" />
        <Stat label="Last hour" value={stats?.last_hour ?? "—"} testid="checkin-hour" />
      </div>

      {stats?.recent?.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-3 flex items-center gap-2">
            <RefreshCw size={11} strokeWidth={1.5} className="opacity-50" />
            <span>Latest arrivals</span>
          </div>
          <div className="space-y-1" data-testid="checkin-recent-list">
            {stats.recent.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{u.first_name}</span>
                  <span className="text-slate-500 ml-2">{u.email}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={u.profile_complete ? "mm-pill mm-pill-mutual" : "mm-pill mm-pill-pass"} style={{ fontSize: 9 }}>
                    {u.profile_complete ? "Complete" : "Pending"}
                  </span>
                  <span className="text-xs text-slate-500 tabular-nums w-16 text-right">{formatRelative(u.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showQR && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "#0F172A" }}
          onClick={() => setShowQR(false)}
          data-testid="qr-modal"
        >
          <button
            onClick={() => setShowQR(false)}
            data-testid="qr-close"
            className="absolute top-6 right-6 text-white/70 hover:text-white"
          >
            <X size={28} strokeWidth={1.5} />
          </button>
          <div className="text-center max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>Muslim Mingle</div>
            <h1 className="font-serif-display text-white text-5xl sm:text-6xl mb-2">Scan to check in</h1>
            <div className="mm-divider-gold mx-auto" style={{ background: "#D4AF37" }} />
            <p className="text-white/70 text-sm mb-10">Aim your phone camera at the code below.</p>
            <div className="inline-block bg-white p-8 rounded-sm">
              <QRCodeSVG
                value={loginURL}
                size={320}
                level="H"
                fgColor="#0F172A"
                bgColor="#FFFFFF"
              />
            </div>
            <p className="text-white/50 text-xs mt-8 break-all tracking-wide">{loginURL}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent, testid }) => (
  <div
    className="border border-slate-200 rounded-sm p-4"
    style={accent ? { borderColor: "#0F172A" } : {}}
    data-testid={testid}
  >
    <div className="overline text-[10px]">{label}</div>
    <div className="font-serif-display text-3xl mt-1" style={accent ? { color: "#0F172A" } : {}}>
      {value}
    </div>
  </div>
);

export default CheckInPanel;
